import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Star, MapPin, ChevronRight, Building2, Clock, Percent, Award,
  Briefcase, Home, UtensilsCrossed, Heart, GraduationCap, Church,
  Palette, DollarSign, Scale, Building, Baby, Monitor, Car, Scissors,
  MoreHorizontal, Loader2
} from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { stripHtml } from "@/components/RichTextEditor";

// Category icons mapping
const CATEGORY_ICONS = {
  "Professional Services": Briefcase,
  "Home Services": Home,
  "Food & Dining": UtensilsCrossed,
  "Health & Wellness": Heart,
  "Education": GraduationCap,
  "Religious Goods": Church,
  "Arts & Crafts": Palette,
  "Financial Services": DollarSign,
  "Legal Services": Scale,
  "Real Estate": Building,
  "Childcare": Baby,
  "Technology": Monitor,
  "Automotive": Car,
  "Beauty & Personal Care": Scissors,
  "Other": MoreHorizontal
};

// Category colors
const CATEGORY_COLORS = {
  "Professional Services": "bg-blue-100 text-blue-700 border-blue-200",
  "Home Services": "bg-orange-100 text-orange-700 border-orange-200",
  "Food & Dining": "bg-red-100 text-red-700 border-red-200",
  "Health & Wellness": "bg-pink-100 text-pink-700 border-pink-200",
  "Education": "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Religious Goods": "bg-purple-100 text-purple-700 border-purple-200",
  "Arts & Crafts": "bg-amber-100 text-amber-700 border-amber-200",
  "Financial Services": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Legal Services": "bg-slate-100 text-slate-700 border-slate-200",
  "Real Estate": "bg-teal-100 text-teal-700 border-teal-200",
  "Childcare": "bg-rose-100 text-rose-700 border-rose-200",
  "Technology": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Automotive": "bg-gray-100 text-gray-700 border-gray-200",
  "Beauty & Personal Care": "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  "Other": "bg-stone-100 text-stone-700 border-stone-200"
};

function StarRating({ rating, size = "sm" }) {
  const stars = [];
  const starSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`${starSize} ${i <= rating ? "text-[var(--liturgical-secondary)] fill-[#D4AF37]" : "text-[#E5E0D5]"}`}
      />
    );
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

function BusinessCard({ listing, onClick }) {
  const CategoryIcon = CATEGORY_ICONS[listing.category] || MoreHorizontal;
  const categoryColor = CATEGORY_COLORS[listing.category] || CATEGORY_COLORS["Other"];
  const openStatus = listing.open_status || {};
  const hasDiscounts = listing.parishioner_discounts && listing.parishioner_discounts.length > 0;
  
  return (
    <Card 
      className="bg-white border-[#E5E0D5] hover:border-[var(--liturgical-primary)]/30 hover:shadow-md transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`business-card-${listing.listing_id}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-3 sm:gap-4">
          {/* Photo/Logo */}
          <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg bg-[#F9F7F2] flex items-center justify-center flex-shrink-0 overflow-hidden relative">
            {listing.photos && listing.photos.length > 0 ? (
              <img 
                src={resolveImageUrl(listing.photos[0])} 
                alt={listing.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <CategoryIcon className="w-8 h-8 text-[#6B665E]" />
            )}
            {listing.is_featured && (
              <div className="absolute top-1 right-1 w-5 h-5 bg-gradient-to-r from-[#D4AF37] to-[#B8972F] rounded-full flex items-center justify-center">
                <Award className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Name and Category */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] truncate group-hover:text-[var(--liturgical-primary)] transition-colors">
                    {listing.name}
                  </h3>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${categoryColor} text-[10px] font-['Nunito']`}>
                    <CategoryIcon className="w-2.5 h-2.5 mr-1" />
                    {listing.category}
                  </Badge>
                  {openStatus.is_open !== null && (
                    <Badge className={`text-[10px] font-['Nunito'] ${openStatus.is_open ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      <Clock className="w-2.5 h-2.5 mr-1" />
                      {openStatus.is_open ? "Open" : "Closed"}
                    </Badge>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#6B665E] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            
            {/* Description */}
            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-2 line-clamp-2">
              {stripHtml(listing.description)}
            </p>
            
            {/* Footer */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-3">
                {/* Rating */}
                {listing.review_count > 0 && (
                  <div className="flex items-center gap-1">
                    <StarRating rating={Math.round(listing.average_rating)} />
                    <span className="text-xs text-[#6B665E] font-['Nunito']">
                      ({listing.review_count})
                    </span>
                  </div>
                )}
                
                {/* Location */}
                <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {listing.distance_miles ? `${listing.distance_miles} mi` : listing.service_area}
                </span>
                
                {/* Parishioner Discount Badge */}
                {hasDiscounts && (
                  <Badge className="bg-purple-100 text-purple-700 text-[10px] font-['Nunito']">
                    <Percent className="w-2.5 h-2.5 mr-1" />
                    Discount
                  </Badge>
                )}
              </div>
              
              {/* Parish Badge */}
              {listing.owner_parish_name && (
                <Badge variant="outline" className="text-[10px] border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)] font-['Nunito']">
                  <Church className="w-2.5 h-2.5 mr-1" />
                  {listing.owner_parish_name.split(" ").slice(0, 2).join(" ")}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DirectoryList({ 
  listings = [], 
  loading = false, 
  searchQuery = "",
  onListingClick 
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
        <p className="text-[#6B665E] font-['Nunito']">No businesses found</p>
        {searchQuery && (
          <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
            Try adjusting your search or filters
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {listings.map(listing => (
        <BusinessCard 
          key={listing.listing_id} 
          listing={listing}
          onClick={() => onListingClick(listing)}
        />
      ))}
    </div>
  );
}
